import { useEffect, useMemo, useState } from "react";
import { usePostHog } from "@posthog/react";
import useAdultGigEditor from "../adult/gigs/useAdultGigEditor";
import { useAuth } from "../../hooks/useAuth";
import { adultDashboardApi } from "../../lib/adultDashboard";
import { captureTeenReviewSubmitted } from "../../lib/analytics";
import { reviewsApi } from "../../lib/reviews";
import type { CancelledGigHistoryItemResponse } from "../../types/cancelledGigTypes";
import type { AdultDashboardCompletedGigItemResponse } from "../../types/adultDashboardTypes";
import type { ResolvedGigIssueItemResponse } from "../../types/resolvedIssueTypes";
import type { CreateReviewRequest, ReviewCandidateResponse, ReviewResponse } from "../../types/reviewTypes";
import { apiErrorToMessage } from "../../utils/ApiUtils";
import CreateGigModal from "../gigs/creategig";
import Button from "../ui/Button/Button";
import Toast, { type ToastTone } from "../ui/Toast/Toast";
import CompletedGigsReviewsWorkspace, {
  type CombinedCompletedGigItem,
  type CombinedCancelledGigItem,
  type CombinedResolvedIssueItem,
} from "./CompletedGigsReviewsWorkspace";

type ToastState = { message: string; tone: ToastTone } | null;

const AdultReviewsWorkspace = () => {
  const posthog = usePostHog();
  const { user } = useAuth();
  const [receivedReviews, setReceivedReviews] = useState<ReviewResponse[]>([]);
  const [submittedReviews, setSubmittedReviews] = useState<ReviewResponse[]>([]);
  const [completedGigs, setCompletedGigs] = useState<AdultDashboardCompletedGigItemResponse[]>([]);
  const [resolvedIssues, setResolvedIssues] = useState<ResolvedGigIssueItemResponse[]>([]);
  const [cancelledGigs, setCancelledGigs] = useState<CancelledGigHistoryItemResponse[]>([]);
  const [reviewCandidates, setReviewCandidates] = useState<ReviewCandidateResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState>(null);

  const { modalProps, openEditor } = useAdultGigEditor({
    analyticsSource: "adult_reviews",
    analyticsEntryPointByMode: {
      repost: "adult_reviews_completed_gig",
    },
    onError: (message) => setToast({ tone: "error", message }),
    onSuccess: async () => {
      const refreshedCompletedGigs = await adultDashboardApi.listCompletedGigs();
      setCompletedGigs(refreshedCompletedGigs);
      setToast({ tone: "success", message: "Gig updated and reposted." });
    },
  });

  useEffect(() => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    let mounted = true;

    const loadWorkspace = async () => {
      try {
        setLoading(true);
        setErrorMessage(null);

        const [received, submitted, completed, resolved, cancelled, candidates] = await Promise.all([
          reviewsApi.listUserReviews(user.id, "POSTER_EXPERIENCE"),
          reviewsApi.listMySubmitted(),
          adultDashboardApi.listCompletedGigs(),
          adultDashboardApi.listResolvedIssues(),
          adultDashboardApi.listCancelledGigs(),
          reviewsApi.listMyCandidates(),
        ]);

        if (!mounted) {
          return;
        }

        setReceivedReviews(received);
        setSubmittedReviews(submitted.filter((review) => review.reviewContext === "WORKER_PERFORMANCE"));
        setCompletedGigs(completed);
        setResolvedIssues(resolved);
        setCancelledGigs(cancelled);
        setReviewCandidates(candidates.filter((candidate) => candidate.requiredContext === "WORKER_PERFORMANCE"));
      } catch (error) {
        if (!mounted) {
          return;
        }

        setErrorMessage(apiErrorToMessage(error, "We couldn't load the completed gigs and reviews workspace."));
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    void loadWorkspace();

    return () => {
      mounted = false;
    };
  }, [user?.id]);

  const normalizedCompletedGigs = useMemo<CombinedCompletedGigItem[]>(() => {
    const candidateByRequestId = new Map(reviewCandidates.map((candidate) => [candidate.gigRequestId, candidate]));
    const submittedReviewByRequestId = new Map(submittedReviews.map((review) => [review.gigRequestId, review]));

    return completedGigs.map((gig) => {
      const candidate = candidateByRequestId.get(gig.requestId) ?? null;
      const submittedReview = submittedReviewByRequestId.get(gig.requestId) ?? null;
      const counterpartName =
        candidate
          ? [candidate.revieweeFirstName, candidate.revieweeLastName].filter(Boolean).join(" ")
          : [submittedReview?.revieweeFirstName, submittedReview?.revieweeLastName].filter(Boolean).join(" ");

      return {
        requestId: gig.requestId,
        gigId: gig.gigId,
        gigTitle: gig.gigTitle,
        paymentAmountCents: gig.paymentAmountCents,
        volunteer: gig.volunteer,
        teensRequired: gig.teensRequired,
        coveredSeats: gig.coveredSeats,
        completedAt: gig.completedAt,
        counterpartName: counterpartName || null,
        submittedReview,
        reviewStatus: candidate ? "needs_review" : "reviewed",
      };
    });
  }, [completedGigs, reviewCandidates, submittedReviews]);

  const normalizedResolvedIssues = useMemo<CombinedResolvedIssueItem[]>(
    () =>
      resolvedIssues.map((issue) => ({
        issueId: issue.issueId,
        gigId: issue.gigId,
        gigTitle: issue.gigTitle,
        counterpartName: [issue.counterpartFirstName, issue.counterpartLastName].filter(Boolean).join(" ") || null,
        reportedBySelf: issue.reportedByRole === "ADULT",
        issueScope: issue.issueScope,
        coveredSeats: issue.coveredSeats,
        status: issue.status,
        resolvedAt: issue.resolvedAt,
        resolutionReason: issue.resolutionReason,
      })),
    [resolvedIssues],
  );

  const normalizedCancelledGigs = useMemo<CombinedCancelledGigItem[]>(
    () =>
      cancelledGigs.map((gig) => ({
        requestId: gig.gigRequestId,
        gigId: gig.gigId,
        gigTitle: gig.gigTitle,
        paymentAmountCents: gig.paymentAmountCents,
        volunteer: gig.volunteer,
        teensRequired: gig.teensRequired,
        coveredSeats: gig.coveredSeats,
        paymentStatus: gig.paymentStatus,
        cancelledAt: gig.cancelledAt,
        refundedAt: gig.refundedAt,
        counterpartName: [gig.counterpartFirstName, gig.counterpartLastName].filter(Boolean).join(" ") || null,
        cancelledBySelf: gig.cancelledByRole === "ADULT",
      })),
    [cancelledGigs],
  );

  const handleSubmitReview = async (gig: CombinedCompletedGigItem, payload: CreateReviewRequest) => {
    const createdReview = await reviewsApi.create(gig.requestId, {
      ...payload,
      reviewContext: "WORKER_PERFORMANCE",
    });
    captureTeenReviewSubmitted(posthog, {
      request_id: gig.requestId,
      gig_id: gig.gigId,
      rating: payload.rating,
    });

    setSubmittedReviews((current) => [createdReview, ...current]);
    setReviewCandidates((current) => current.filter((candidate) => candidate.gigRequestId !== gig.requestId));
  };

  return (
    <>
      <Toast
        isOpen={toast !== null}
        tone={toast?.tone ?? "info"}
        message={toast?.message ?? ""}
        onClose={() => setToast(null)}
      />
      <CompletedGigsReviewsWorkspace
        copy={{
          eyebrow: "Reviews",
          title: "Completed Gigs, Reviews, and Issues",
          intro:
            "Track the feedback teens left about working with you and keep completed gigs moving by finishing any worker reviews that are still missing.",
          receivedLabel: "Received reviews",
          completedLabel: "Completed gigs",
        needsReviewLabel: "Needs your review",
        resolvedLabel: "Resolved issues",
        cancelledLabel: "Cancelled gigs",
        completedSummary: "You've reviewed {reviewedCount} completed gigs so far.",
        needsReviewSummary: "Finish feedback directly from the completed gigs tab.",
        resolvedSummary: "{resolvedCount} issue decisions are in your account history.",
        cancelledSummary: "{cancelledCount} cancelled gigs are in your account history.",
        completedTabLabel: "Completed Gigs",
        receivedTabLabel: "Reviews You Received",
        resolvedTabLabel: "Resolved Issues",
        cancelledTabLabel: "Cancelled Gigs",
        completedTabTitle: "Completed gigs with review status",
        completedTabDescription:
          "Review completed work, spot gigs missing your feedback, and submit worker reviews without leaving this page.",
        receivedTabTitle: "Feedback teens left about you",
        receivedTabDescription:
          "Look back on poster-experience feedback tied to your completed gigs, with filters for rating, dates, and keywords.",
        resolvedTabTitle: "Issues closed after review",
        resolvedTabDescription:
          "See how support resolved payout and refund decisions tied to your gigs, with the final outcome and reason kept in one place.",
        cancelledTabTitle: "Cancelled gigs and refunds",
        cancelledTabDescription:
          "Keep cancelled scheduled gigs in your account history, including whether the payment was refunded.",
        receivedReviewTitle: "Poster-experience review",
        missingReviewCallout: "This completed gig is still waiting on your review.",
        missingReviewDescription:
          "Add a worker-performance review here so the gig and feedback history stay together.",
        reviewSubmittedLabel: "Your review",
          reviewModalTitle: (gig) => `How was your gig with ${gig.counterpartName ?? "this teen"}?`,
          reviewModalPlaceholder: "Share how the teen handled the work, communication, and follow-through.",
          counterpartFallbackLabel: "Teen worker",
        }}
      receivedReviews={receivedReviews}
      completedGigs={normalizedCompletedGigs}
      resolvedIssues={normalizedResolvedIssues}
      cancelledGigs={normalizedCancelledGigs}
      loading={loading}
      errorMessage={errorMessage}
      onSubmitReview={handleSubmitReview}
        renderCompletedGigActions={(gig) => {
          const gigId = gig.gigId;

          return gigId ? (
            <Button
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => {
                setToast(null);
                void openEditor(gigId, "repost");
              }}
            >
              Edit and repost
            </Button>
          ) : null;
        }}
      />
      <CreateGigModal {...modalProps} />
    </>
  );
};

export default AdultReviewsWorkspace;
